import { LiveStreamType, SupportedRegions } from "@nova/constants"
import { z } from "zod"
import { droneSchema } from "../pubsub/drones"

export const liveVideoState = z.discriminatedUnion("state", [
    z.object({
        state: z.literal("waiting"),
        live: z.literal(false),
        ended: z.literal(false),
        shutdown: z.literal(false),
    }),
    z.object({
        state: z.literal("streaming"),
        live: z.literal(true),
        ended: z.literal(false),
        shutdown: z.literal(false),
    }),
    z.object({
        state: z.literal("disconnected"),
        live: z.literal(false),
        ended: z.literal(true),
        shutdown: z.literal(false),
    }),
    z.object({
        state: z.literal("shutdown"),
        live: z.literal(false),
        ended: z.literal(true),
        shutdown: z.literal(true),
    }),
])

export type LiveVideoState = z.infer<typeof liveVideoState>

export const liveVideoStates = {
    waiting: { state: "waiting", live: false, ended: false, shutdown: false },
    streaming: { state: "streaming", live: true, ended: false, shutdown: false },
    disconnected: { state: "disconnected", live: false, ended: true, shutdown: false },
    shutdown: { state: "shutdown", live: false, ended: true, shutdown: true },
} satisfies Record<LiveVideoState["state"], LiveVideoState>

export const liveVideoStateNameSchema = z.enum(
    Object.keys(liveVideoStates) as [LiveVideoState["state"], ...LiveVideoState["state"][]]
)

export const liveStreamSummarySchema = droneSchema.extend({
    createdAt: z.number(),
    state: liveVideoStateNameSchema,
    pilotName: z.string().optional(),
})

export type LiveStreamSummary = z.infer<typeof liveStreamSummarySchema>

export const liveVideoMetadataSchema = z.object({
    workspaceId: z.string(),
    projectId: z.string(),
    flightId: z.string(),
    region: z.enum(SupportedRegions),
    createdAt: z.number(),
    createdBy: z.object({ userId: z.string() }),
    saveRecording: z.boolean(),
    licenseId: z.string().optional(),
    status: liveVideoState,
    startedAt: z.number().optional(),
    isMobile: z.boolean().optional(),
    location: z.object({ latitude: z.number(), longitude: z.number() }).optional(),
    name: z.string(),
    sourceType: z.enum(LiveStreamType).catch(LiveStreamType.Drone),
    pilotName: z.string().optional(),
    recordingStatus: z.union([
        z.literal("disabled"),
        z.literal("waiting"),
        z.literal("in_progress"),
        z.literal("complete"),
    ]),
    m3u8Prefixes: z.array(z.string()).default([]),
    missionGroupIds: z.array(z.string()).default([]),
    lastEventTimestamp: z.number(),
    recordingPrefix: z.string().optional(),
    recordAudio: z.boolean().optional(),
    recording: z
        .object({
            resourceId: z.string(),
            sid: z.string(),
            uid: z.string(),
            mode: z.enum(["individual", "mix"]),
        })
        .optional(),
    DEPRECATED_startedByClient: z.boolean().optional(),
})

export type LiveVideoMetadata = z.infer<typeof liveVideoMetadataSchema>
export type LiveVideoRecordingHandle = NonNullable<LiveVideoMetadata["recording"]>
